import React, { useEffect, useState, useRef } from "react";
import "./CookieBanner.css";
import CookieSettingsModal from "./CookieSettingsModal";

const STORAGE_KEY = "cookie_consent_v1";

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [consent, setConsent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const acceptRef = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        setShow(true);
      } else {
        setConsent(JSON.parse(saved));
        setShow(false);
      }
    } catch (e) {
      // if localStorage is not available, show banner as fallback
      setShow(true);
    }
  }, []);

  // listen for consent changes from other UI (e.g., Footer modal) and update state
  useEffect(() => {
    const handler = (e) => {
      const newConsent = e?.detail;
      if (newConsent) {
        setConsent(newConsent);
        setShow(false);
      }
    };
    window.addEventListener("cookieConsentUpdated", handler);
    return () => window.removeEventListener("cookieConsentUpdated", handler);
  }, []);

  // focus Accept button when banner appears for keyboard users
  useEffect(() => {
    if (show) {
      const t = setTimeout(() => acceptRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [show]);

  const saveConsent = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (e) {}
    // notify other parts of the site of the change
    try {
      window.dispatchEvent(
        new CustomEvent("cookieConsentUpdated", { detail: value })
      );
    } catch (e) {}
    setConsent(value);
    setShow(false);
  };

  const acceptAll = () => {
    const value = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    };
    saveConsent(value);
  };

  const decline = () => {
    const value = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    };
    saveConsent(value);
  };

  const openManage = () => {
    // open granular settings modal
    setIsModalOpen(true);
  };

  const closeManage = () => setIsModalOpen(false);

  const handleSaveFromModal = (newConsent) => saveConsent(newConsent);

  // show compact variant on small screens via CSS — still render DOM
  if (!show) return null;

  return (
    <>
      <div
        className="cookie-banner cookie-banner--enter"
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
        aria-describedby="cookie-banner-desc"
      >
        <div className="cookie-banner__content" id="cookie-banner-desc">
          <p className="cookie-banner__text">
            We use cookies to improve your experience. By accepting, you agree
            to the use of analytics and marketing cookies. See our{" "}
            <a className="cookie-banner__link" href="/privacy">
              Privacy Policy
            </a>{" "}
            for details.
          </p>
          <div className="cookie-banner__actions">
            <button
              ref={acceptRef}
              className="cookie-banner__btn cookie-banner__btn--accept"
              onClick={acceptAll}
            >
              Accept
            </button>
            <button
              className="cookie-banner__btn cookie-banner__btn--decline"
              onClick={decline}
            >
              Decline
            </button>
            <button
              className="cookie-banner__btn cookie-banner__btn--manage"
              onClick={openManage}
            >
              Manage
            </button>
          </div>
        </div>
      </div>

      <CookieSettingsModal
        isOpen={isModalOpen}
        onClose={closeManage}
        consent={consent || {}}
        onSave={handleSaveFromModal}
      />
    </>
  );
}
